<template>
  <div class="captcha-container">
    <v-row dense align="center" no-gutters>
      <v-col cols="auto">
        <canvas
          ref="canvas"
          :width="canvasWidth"
          :height="canvasHeight"
          class="captcha-canvas"
        ></canvas>
      </v-col>
      <v-col cols="auto">
        <v-btn
          icon
          color="primary"
          variant="text"
          class="ml-1"
          @click="generate"
          :disabled="locked"
        >
          <v-icon>{{ $vars.V("icon_refresh") }}</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-row dense class="mt-2" align="center" no-gutters>
      <v-col>
        <v-text-field
          v-model="inputValue"
          :placeholder="placeholder"
          variant="outlined"
          density="compact"
          color="primary"
          hide-details
          :disabled="locked"
          @keydown.enter.prevent="verify"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="primary"
          class="ml-2"
          variant="flat"
          size="small"
          :disabled="verifydata !== 'Verify'?true:false"
          @click="verify"
        >
          <v-icon start>mdi-check</v-icon> {{ verifydata }}
        </v-btn>
      </v-col>
    </v-row>

    <div class="mt-1 text-caption">
      <span
        v-if="message"
        :class="{
          'text-error': !verified,
          'text-success': verified
        }"
      >
        {{ message }}
      </span>
      <span v-if="!locked && attemptsLeft !== null" class="text-grey ms-2">
        Attempts left: {{ attemptsLeft }}
      </span>
      <span v-if="locked" class="text-error ms-2">
        Too many attempts — refresh to try again.
      </span>
    </div>
  </div>
</template>

<script>
export default {
  name: "CaptchaCompact",
  props: {
    maxAttempts: { type: Number, default: 3 },
    placeholder: { type: String, default: "Input Answer" },
    canvasWidth: { type: Number, default: 160 },
    canvasHeight: { type: Number, default: 48 },
    difficulty: { type: Number, default: 1 },
  },
  data() {
    return {
      questionText: "",
      answer: null,
      inputValue: "",
      attemptsLeft: this.maxAttempts === null ? null : this.maxAttempts,
      message: "",
      verified: false,
      locked: false,
      verifydata: 'Verify'
    };
  },
  mounted() {
    this.generate();
  },
  methods: {
    randInt(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    },
    makeQuestion() {
      let a, b, op, ans;
      if (this.difficulty <= 1) {
        a = this.randInt(1, 20);
        b = this.randInt(1, 20);
        op = "+";
        ans = a + b;
      } else if (this.difficulty === 2) {
        a = this.randInt(5, 50);
        b = this.randInt(1, 30);
        op = Math.random() < 0.5 ? "+" : "-";
        ans = op === "+" ? a + b : a - b;
      } else {
        const ops = ["+", "-", "*"];
        op = ops[this.randInt(0, ops.length - 1)];
        a = this.randInt(2, 20);
        b = this.randInt(2, 12);
        ans = op === "+" ? a + b : op === "-" ? a - b : a * b;
      }
      return { q: `${a} ${op} ${b} = ?`, ans };
    },
    drawCanvas(text) {
      const c = this.$refs.canvas;
      if (!c) return;
      const ctx = c.getContext("2d");

      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      const g = ctx.createLinearGradient(0, 0, this.canvasWidth, this.canvasHeight);
      g.addColorStop(0, "#e6f7ff");
      g.addColorStop(1, "#114E87");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);

      for (let i = 0; i < 5; i++) {
        ctx.strokeStyle = `rgba(${this.randInt(100,160)},${this.randInt(100,160)},${this.randInt(100,160)},0.3)`;
        ctx.beginPath();
        ctx.moveTo(this.randInt(0, this.canvasWidth), this.randInt(0, this.canvasHeight));
        ctx.lineTo(this.randInt(0, this.canvasWidth), this.randInt(0, this.canvasHeight));
        ctx.stroke();
      }

      const fontSize = this.randInt(20, 26);
      ctx.font = `${fontSize}px Arial`;
      ctx.fillStyle = `rgb(${this.randInt(20,80)},${this.randInt(20,80)},${this.randInt(20,80)})`;
      ctx.textBaseline = "middle";
      let x = 10;
      const y = this.canvasHeight / 2;
      for (let ch of text) {
        const angle = (Math.random() - 0.5) * 0.3;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(angle);
        ctx.fillText(ch, 0, 0);
        ctx.restore();
        x += ctx.measureText(ch).width + this.randInt(2, 6);
      }

      for (let i = 0; i < 20; i++) {
        ctx.fillStyle = `rgba(${this.randInt(0,255)},${this.randInt(0,255)},${this.randInt(0,255)},0.3)`;
        ctx.beginPath();
        ctx.arc(this.randInt(0, this.canvasWidth), this.randInt(0, this.canvasHeight), this.randInt(0,2)+1, 0, Math.PI*2);
        ctx.fill();
      }
    },
    generate() {
      if (this.locked) {
        this.locked = false;
        this.attemptsLeft = this.maxAttempts === null ? null : this.maxAttempts;
      }
      const { q, ans } = this.makeQuestion();
      this.questionText = q;
      this.answer = ans;
      this.inputValue = "";
      this.message = "";
      this.verified = false;
      this.$nextTick(() => this.drawCanvas(q));
    },
    verify() {
      this.verifydata = 'Loading...'
      if (this.locked) return;
      const val = (this.inputValue || "").trim();
      if (val === "") {
        this.message = "Please enter the answer";
        this.verified = false;
        return;
      }
      const numeric = Number(val);
      if (!Number.isFinite(numeric)) {
        this.message = "Answer must be a number";
        this.verified = false;
        return;
      }
      if (numeric === this.answer) {
        this.message = "Verified ✅";
        this.verified = true;
        this.$emit("verified", true);
      } else {
        this.message = "Incorrect answer";
        this.verified = false;
        if (this.attemptsLeft !== null) {
          this.attemptsLeft--;
          if (this.attemptsLeft <= 0) {
            this.locked = true;
            this.message = "Too many attempts — refresh to try again.";
          }
        }
      }
      let data = this
      setTimeout(function(){
        data.verifydata = 'Verify'
        }, 1000)
    },
  },
};
</script>

<style scoped>
.captcha-container {
  max-width: 320px;
  font-family: "Roboto", sans-serif;
}

.captcha-canvas {
  border: 1px solid #b3d4f5;
  border-radius: 4px;
  display: block;
  box-shadow: inset 0 0 3px rgba(17, 78, 135, 0.2);
}

.text-error {
  color: #d32f2f;
}
.text-success {
  color: #388e3c;
}
.text-grey {
  color: #777;
}
</style>
